export const STORAGE_KEY = "adaptive-ai-training-guided:progress:v1";

export interface GuidedProgress {
  version: 1;
  started: boolean;
  lastSlideId: number;
  completedSlideIds: number[];
  audioPositions: Record<string, number>;
  playbackSpeed: number;
  volume: number;
  muted: boolean;
  captionsEnabled: boolean;
  transcriptOpen: boolean;
  courseCompleted: boolean;
  updatedAt: string;
}

export function createInitialProgress(): GuidedProgress {
  return {
    version: 1,
    started: false,
    lastSlideId: 1,
    completedSlideIds: [],
    audioPositions: {},
    playbackSpeed: 1,
    volume: 0.8,
    muted: false,
    captionsEnabled: true,
    transcriptOpen: true,
    courseCompleted: false,
    updatedAt: new Date().toISOString(),
  };
}

const finiteNumber = (
  value: unknown,
  fallback: number,
  minimum: number,
  maximum: number,
): number =>
  typeof value === "number" && Number.isFinite(value)
    ? Math.min(Math.max(value, minimum), maximum)
    : fallback;

export function loadProgress(): GuidedProgress {
  const fallback = createInitialProgress();

  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return fallback;
    }

    const parsed = JSON.parse(rawValue) as Partial<GuidedProgress>;
    if (parsed.version !== 1) {
      return fallback;
    }

    const audioPositions = Object.fromEntries(
      Object.entries(parsed.audioPositions ?? {})
        .filter(
          ([key, value]) =>
            /^\d+$/.test(key) && typeof value === "number" && Number.isFinite(value),
        )
        .map(([key, value]) => [key, Math.max(0, value as number)]),
    );

    return {
      version: 1,
      started: Boolean(parsed.started),
      lastSlideId: Math.max(
        1,
        Math.floor(finiteNumber(parsed.lastSlideId, 1, 1, Number.MAX_SAFE_INTEGER)),
      ),
      completedSlideIds: [
        ...new Set(
          (parsed.completedSlideIds ?? []).filter(
            (id): id is number => Number.isInteger(id) && id > 0,
          ),
        ),
      ],
      audioPositions,
      playbackSpeed: finiteNumber(parsed.playbackSpeed, 1, 0.75, 2),
      volume: finiteNumber(parsed.volume, 0.8, 0, 1),
      muted: Boolean(parsed.muted),
      captionsEnabled:
        parsed.captionsEnabled === undefined
          ? fallback.captionsEnabled
          : Boolean(parsed.captionsEnabled),
      transcriptOpen:
        parsed.transcriptOpen === undefined
          ? fallback.transcriptOpen
          : Boolean(parsed.transcriptOpen),
      courseCompleted: Boolean(parsed.courseCompleted),
      updatedAt:
        typeof parsed.updatedAt === "string"
          ? parsed.updatedAt
          : fallback.updatedAt,
    };
  } catch {
    return fallback;
  }
}

export function saveProgress(progress: GuidedProgress): void {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...progress, updatedAt: new Date().toISOString() }),
    );
  } catch {
    // Progress persistence is an enhancement; playback remains usable if
    // storage is unavailable or has been disabled by the learner.
  }
}

export function restartProgress(
  previous: GuidedProgress,
  firstSlideId: number,
): GuidedProgress {
  return {
    ...createInitialProgress(),
    started: true,
    lastSlideId: firstSlideId,
    playbackSpeed: previous.playbackSpeed,
    volume: previous.volume,
    muted: previous.muted,
    captionsEnabled: previous.captionsEnabled,
    transcriptOpen: previous.transcriptOpen,
  };
}
